import { Devices } from '@weenat/client/dist/enums'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import Text from 'app/src/kit/Text'
import { useToggleFeature } from 'app/state'
import isNil from 'lodash-es/isNil'
import moment from 'moment-timezone'
import Icons from './Icons'
import LastUpdateBadge from './LastUpdateBadge'

const Icon = Icons.Update

/**
 * @deprecated Remove this component when the `marker_redesign` feature flag is cleaned up.
 */
interface UpdateInformationTimingProps {
  /**
   * @deprecated Remove this props when the `marker_redesign` feature flag is cleaned up.
   * @default false
   */
  isVirtualMetric?: boolean
  timestamp: number | undefined | null
  warningThreshold?: number
}

/**
 * @deprecated Use `LastUpdate` instead. Remove this component when the `marker_redesign` feature flag is cleaned up.
 */
export const UpdateInformationTiming: FC<UpdateInformationTimingProps> = ({
  timestamp,
  isVirtualMetric = false,
  warningThreshold
}) => {
  const { formatRelativeTimeToNow } = useIntl()

  if (isNil(timestamp) || !isFinite(timestamp)) {
    return (
      <>
        <Icon $color={'grayscale.700'} $size='rg' />
        <Text $lineHeight='none' $fontSize={'sm'} $color={'grayscale.700'}>
          -
        </Text>
      </>
    )
  }

  const warningThresholdToUse = !isNil(warningThreshold)
    ? warningThreshold
    : isVirtualMetric
      ? Devices.UpdateWarningThresholds.virtualDevices
      : Devices.UpdateWarningThresholds.default

  const minutesSinceLastUpdate = moment().diff(moment.unix(timestamp), 'minutes')
  const thresholdExceeded = minutesSinceLastUpdate > warningThresholdToUse
  const color = thresholdExceeded ? 'data.danger' : 'grayscale.700'

  return (
    <>
      <Icon $color={color} $size='rg' />
      <Text $lineHeight='none' $fontSize={'sm'} $color={color}>
        {formatRelativeTimeToNow(timestamp)}
      </Text>
    </>
  )
}

export interface UpdateInformationProps {
  className?: string
  /**
   * @deprecated Remove this props when the `marker_redesign` feature flag is cleaned up.
   * @default false
   */
  isVirtualMetric?: boolean
  /**
   * Date of the update in a unix timestamp format
   * @default null
   */
  timestamp: number | null | undefined
  /**
   * Timezone of the ressource
   * @default undefined
   */
  timezone: string | undefined
  /**
   * Make it required when the `marker_redesign` feature flag is cleaned up.
   */
  warningThreshold?: number
  /**
   * Make it required when the `marker_redesign` feature flag is cleaned up.
   */
  dangerThreshold?: number
  /**
   * Name of the data provider (usually the device technical name)
   */
  name?: string
  /**
   * Provide it to create a link towards the ressource.
   */
  deviceId?: number
}

const UpdateInformation: FC<UpdateInformationProps> = ({
  isVirtualMetric = false,
  timestamp = null,
  timezone,
  warningThreshold,
  dangerThreshold,
  className,
  name,
  deviceId
}) => {
  const { t } = useIntl()
  const org = useOrgContext()
  const currentOrgId = org.id
  const [{ marker_redesign }] = useToggleFeature()

  const informationOrigin = (
    <Text $lineHeight='none' $fontSize='sm' $color={'grayscale.700'}>
      {!isNil(name)
        ? name
        : isNil(timestamp)
          ? t('summaries.metric_providers.none', { capitalize: true })
          : null}
    </Text>
  )

  return (
    <Flex className={className} $alignItems='center' $gap='sm'>
      {!isNil(deviceId) ? (
        <a href={`/farms/${currentOrgId}/admin/devices/${deviceId}`}>{informationOrigin}</a>
      ) : (
        informationOrigin
      )}
      {marker_redesign ? (
        <LastUpdateBadge
          timestamp={timestamp}
          timezone={timezone}
          warningThreshold={warningThreshold}
          dangerThreshold={dangerThreshold}
        />
      ) : (
        <UpdateInformationTiming
          timestamp={timestamp}
          isVirtualMetric={isVirtualMetric}
          warningThreshold={warningThreshold}
        />
      )}
    </Flex>
  )
}

export default UpdateInformation
